import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { matchIsValidTel } from 'mui-tel-input';
import api from '@/lib/api';
import { toast } from '@/lib/toast';
import { applyGoogleFonts } from '@/lib/branding';
import { buildTheme, DEFAULT_BRANDING } from '@/theme';
import LeadFormRenderer from '@/components/LeadFormRenderer';

// UTM params can land in either the real query string
// (example.com/?utm_source=ig#/f/token) or inside the hash fragment
// (example.com/#/f/token?utm_source=ig) since the app uses HashRouter.
// Harvest utm_* keys from both; the backend whitelists the known five.
const collectUtm = () => {
  const utm = {};
  const harvest = (qs) => {
    if (!qs) return;
    new URLSearchParams(qs).forEach((value, key) => {
      const k = key.toLowerCase();
      if (k.startsWith('utm_') && !(k in utm)) utm[k] = value;
    });
  };
  harvest(window.location.search);
  const hash = window.location.hash || '';
  const qIdx = hash.indexOf('?');
  if (qIdx !== -1) harvest(hash.slice(qIdx + 1));
  return Object.keys(utm).length > 0 ? utm : null;
};

// Public lead-capture form. No login — the tenant + form are resolved from the
// unguessable token in the URL (shared as a link or QR code).
const PublicLeadFormPage = () => {
  const { token } = useParams();
  const [state, setState] = useState({ status: 'loading' });
  const [values, setValues] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    notes: '',
    custom: {},
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null); // { message, redirectUrl }
  // Honeypot: humans never see or fill this field; bots that auto-fill every
  // input will, letting the backend silently discard the submission.
  const [website, setWebsite] = useState('');
  // Capture UTM params once on mount — SPA navigation can rewrite the hash
  // (and its query string) before the visitor finally submits.
  const utmRef = useRef(null);
  if (utmRef.current === null) utmRef.current = { utm: collectUtm() };
  const redirectTimerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get(`/public/lead-form/${token}`)
      .then((res) => {
        if (!cancelled) setState({ status: 'ok', data: res.data });
      })
      .catch((err) => {
        if (!cancelled)
          setState({
            status: 'error',
            message: err.response?.data?.detail || 'This form link is invalid or was replaced.',
          });
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => () => clearTimeout(redirectTimerRef.current), []);

  const leadFields = Array.isArray(state.data?.lead_fields) ? state.data.lead_fields : [];
  const businessName = state.data?.app_name || state.data?.tenant_name || '';
  const formSettings = state.data?.form_settings || {};
  const title = formSettings.title || businessName;
  const intro = formSettings.intro || "Leave your details and we'll get back to you.";
  const showNotes = Boolean(formSettings.show_notes);

  // The GET response carries the form tenant's full public branding. Build a
  // page-local theme from it so the form renders with that tenant's look even
  // when the app-level ThemeProvider resolved a different tenant.
  const branding = useMemo(
    () => ({ ...DEFAULT_BRANDING, ...(state.status === 'ok' ? state.data : null) }),
    [state],
  );
  const mode = branding.default_mode === 'light' ? 'light' : 'dark';
  const theme = useMemo(() => buildTheme(branding, mode), [branding, mode]);
  const logoUrl = mode === 'dark' ? branding.logo_dark_url || branding.logo_url : branding.logo_url;

  useEffect(() => {
    if (state.status === 'ok') applyGoogleFonts(branding, 'public-form-google-fonts');
  }, [state.status, branding]);

  const handleChange = (patch) => setValues((prev) => ({ ...prev, ...patch }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (values.phone && !matchIsValidTel(values.phone)) {
      toast.error('Enter a valid phone number, including country code');
      return;
    }
    setSubmitting(true);
    try {
      const customFields = { ...values.custom };
      for (const field of leadFields) {
        if (
          field.type === 'number' &&
          customFields[field.key] !== undefined &&
          customFields[field.key] !== ''
        ) {
          const n = Number(customFields[field.key]);
          if (!Number.isNaN(n)) customFields[field.key] = n;
        }
      }
      const res = await api.post(`/public/lead-form/${token}`, {
        first_name: values.first_name,
        last_name: values.last_name || null,
        email: values.email,
        phone: values.phone || null,
        notes: values.notes || '',
        custom_fields: customFields,
        utm: utmRef.current.utm,
        website,
      });
      const redirectUrl = res.data?.redirect_url || null;
      setDone({
        message: res.data?.message || 'Thanks! We will be in touch.',
        redirectUrl,
      });
      if (redirectUrl) {
        // Let the visitor read the thank-you before sending them onward.
        redirectTimerRef.current = setTimeout(() => {
          window.location.assign(redirectUrl);
        }, 2500);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit — please try again');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ThemeProvider theme={theme}>
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        p: 2,
      }}
    >
      <Paper variant="outlined" sx={{ p: { xs: 3, sm: 4 }, maxWidth: 460, width: '100%' }}>
        {state.status === 'loading' && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {state.status === 'error' && (
          <Box sx={{ textAlign: 'center' }}>
            <ErrorOutlineIcon sx={{ fontSize: 44, color: 'error.main', mb: 1 }} />
            <Typography variant="body1">{state.message}</Typography>
          </Box>
        )}

        {state.status === 'ok' && done && (
          <Box sx={{ textAlign: 'center' }}>
            <CheckCircleIcon sx={{ fontSize: 44, color: 'success.main', mb: 1 }} />
            <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
              {done.message}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              — {businessName}
            </Typography>
            {done.redirectUrl && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                Redirecting you in a moment…
              </Typography>
            )}
          </Box>
        )}

        {state.status === 'ok' && !done && (
          <form onSubmit={handleSubmit}>
            {logoUrl && (
              <Box
                component="img"
                src={logoUrl}
                alt={`${businessName} logo`}
                sx={{ maxHeight: 48, maxWidth: '100%', objectFit: 'contain', display: 'block', mb: 1 }}
              />
            )}
            <Typography
              variant="h5"
              fontWeight={700}
              color="primary"
              sx={{ mb: 0.5, overflowWrap: 'break-word' }}
            >
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {intro}
            </Typography>
            <Stack spacing={2}>
              <LeadFormRenderer
                fields={leadFields}
                values={values}
                onChange={handleChange}
                form={formSettings}
                showNotes={showNotes}
              />
              {/* Honeypot — invisible to humans, auto-filled by naive bots. */}
              <Box
                aria-hidden="true"
                sx={{ position: 'absolute', left: '-9999px', height: 0, overflow: 'hidden' }}
              >
                <input
                  type="text"
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                />
              </Box>
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={submitting}
                data-testid="public-submit-button"
              >
                {submitting ? 'Sending…' : 'Send'}
              </Button>
            </Stack>
          </form>
        )}
      </Paper>
    </Box>
    </ThemeProvider>
  );
};

export default PublicLeadFormPage;
